import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Text } from '@mantine/core'
import { BILLING_DISPLAY_TIMEZONE } from '@/pages/Billing/utils'
import { CASE_EVENT_ACTION_VALUES, type CaseEventAction } from '@/model/middleware-types'

dayjs.extend(utc)
dayjs.extend(timezone)

// Compact audit list over the case's billing_case_event rows (newest-first from
// the API). Structural mirror of caseEventSchema in case-detail.contract.ts.
export type CaseEvent = {
  actor?: string
  action: string
  oldValue?: unknown
  newValue?: unknown
  createdAt: string
}

const ACTION_LABELS: Record<CaseEventAction, string> = {
  case_created: 'Case created',
  claim_status_changed: 'Claim status changed',
  entity_changed: 'Billing entity changed',
  eob_entered: 'EOB entered',
  payment_recorded: 'Payment recorded',
  payment_failed: 'Payment failed',
  payment_refunded: 'Payment refunded',
  claim_refreshed: 'Claim refreshed',
  case_voided: 'Case voided',
  route_changed: 'Route changed',
  claim_submitted: 'Claim submitted',
  claim_blocked: 'Claim blocked',
  claim_failed: 'Claim failed',
}

function actionLabel(action: string): string {
  if ((CASE_EVENT_ACTION_VALUES as readonly string[]).includes(action)) {
    return ACTION_LABELS[action as CaseEventAction]
  }
  return action.replace(/_/g, ' ')
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return ['string', 'number', 'boolean'].includes(typeof value)
}

// Extra context line for the four claim-submission actions, whose new_value is a
// structured object rather than a plain value — so the oldValue → newValue transition
// below never fires for them. Mirrors that same raw-value idiom (no invented labels):
// just the one field a biller cares about, straight out of new_value.
function eventDetail(event: CaseEvent): string | null {
  const value = event.newValue as Record<string, unknown> | undefined
  if (event.action === 'claim_submitted' && typeof value?.serviceId === 'string') {
    // Present when the biller submitted under another practitioner (submit-claim.contract.ts).
    const submittedAs = value.submittedAs as { display?: string } | undefined
    return submittedAs?.display ? `${value.serviceId} · as ${submittedAs.display}` : value.serviceId
  }
  if (event.action === 'claim_blocked' && Array.isArray(value?.tasks)) {
    return value.tasks.join(', ')
  }
  if (event.action === 'claim_failed' && typeof value?.reason === 'string') {
    return value.reason
  }
  if (event.action === 'route_changed' && typeof value?.submissionRoute === 'string') {
    return value.submissionRoute
  }
  return null
}

export function EventHistory({ events }: { events: CaseEvent[] }) {
  if (events.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No events yet
      </Text>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {events.map((event, index) => {
        const detail = eventDetail(event)
        return (
          <div key={index} className="flex flex-col">
            <div className="flex items-baseline justify-between gap-2">
              <Text size="sm" c="dark">
                {actionLabel(event.action)}
              </Text>
              <Text size="xs" c="dimmed" className="whitespace-nowrap">
                {dayjs(event.createdAt).tz(BILLING_DISPLAY_TIMEZONE).format('MM/DD/YYYY h:mm A')}
              </Text>
            </div>
            {isPrimitive(event.oldValue) && isPrimitive(event.newValue) ? (
              <Text size="xs" c="dimmed">
                {`${event.oldValue} → ${event.newValue}`}
              </Text>
            ) : null}
            {detail ? (
              <Text size="xs" c="dimmed">
                {detail}
              </Text>
            ) : null}
            {event.actor ? (
              <Text size="xs" c="dimmed">
                {event.actor}
              </Text>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
